import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import axios from "axios";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { convertFromTimeStampTToDate } from "../../../helpers/TimeConvert";
import { useEventContext } from "../Components/EventDataContext";
import { EventTableActions } from "./ActionFormatter/EventTableActions";
import { EventAttendanceDialog } from "./EventAttendanceDialog";
import {
  answerColumns,
  attendeeAnswer,
  attendeePaymentStatus,
  downloadAttendeesXlsx,
  formatPaymentMethod,
  isFreeEvent,
} from "./attendeesExport";
import moment from "moment";
import { Badge, Spinner } from "react-bootstrap";
const useStyles = makeStyles((theme) => ({
  greyRatio: {
    color: "black",
    background: "#D3D3D3",
  },
  warningRatio: {
    color: "white",
    background: "#eed202",
  },
  dangerRatio: {
    color: "white",
    background: "#F32013",
  },
  successRatio: {
    color: "white",
    background: "#4BB543",
  },
  tableWrapper: {
    overflowX: "auto",
  },
}));

const attendeeDetailsUrl = "api/workshops/attendee-details/";

export default function EventRow(props) {
  const classes = useStyles();
  const { row, reset, setReset, showActions, columnCount } = props;
  const [open, setOpen] = useState(false);
  // status: "idle" | "loading" | "error" | "loaded"
  const [attendees, setAttendees] = useState({ status: "idle", data: null, forbidden: false });
  const [retryCount, setRetryCount] = useState(0);
  const eventContext = useEventContext();
  // Sent explicitly for the same reason as EventList.js / EventDataContext.js:
  // axios.defaults only gains x-auth-token after the rehydrate saga resolves.
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);
  const isOpen = open && !reset;

  const handleRowClick = () => {
    // After a page change the table sets `reset`, which already shows this
    // row collapsed, so the next click opens it rather than toggling.
    setOpen(reset ? true : !open);
    reset && setReset(false);
  };

  const retry = useCallback(() => setRetryCount((n) => n + 1), []);

  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [exportDownloading, setExportDownloading] = useState(false);
  // null | "error" | "forbidden"
  const [exportError, setExportError] = useState(null);

  // Fetches its own copy of the roster so the row needn't be expanded first.
  const downloadAttendees = async () => {
    if (exportDownloading) return;
    setExportDownloading(true);
    setExportError(null);
    try {
      const result = await axios(attendeeDetailsUrl + row.workshop_id, {
        headers: { "x-auth-token": authToken },
      });
      downloadAttendeesXlsx(row, result.data.results);
    } catch (e) {
      console.error(e);
      setExportError(e.response?.status === 403 ? "forbidden" : "error");
    } finally {
      setExportDownloading(false);
    }
  };

  const free = isFreeEvent(row);
  const paymentBadgeClass = (status) =>
    status === "Paid"
      ? classes.successRatio
      : status === "Not paid"
      ? classes.dangerRatio
      : classes.greyRatio;
  const paymentCells = (attendee) => {
    const status = attendeePaymentStatus(row, attendee);
    // Payment details only for "Paid": a free event's $0 checkout has none worth showing.
    const paid = status === "Paid";
    return (
      <>
        <TableCell>
          <Badge pill className={paymentBadgeClass(status)}>
            {status}
          </Badge>
        </TableCell>
        <TableCell>
          {paid && attendee.payment_date
            ? moment(attendee.payment_date, "YYYY-MM-DD").format("MMM D, YYYY")
            : "—"}
        </TableCell>
        <TableCell>{(paid && formatPaymentMethod(attendee)) || "—"}</TableCell>
      </>
    );
  };

  // Refetch on every expand, and while open whenever the list's counts for
  // this workshop change, so the panel never shows a stale roster or
  // payment status.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setAttendees({ status: "loading", data: null, forbidden: false });
    axios(attendeeDetailsUrl + row.workshop_id, {
      headers: { "x-auth-token": authToken },
    })
      .then((result) => {
        if (!cancelled) {
          setAttendees({ status: "loaded", data: result.data.results, forbidden: false });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setAttendees({
          status: "error",
          data: null,
          forbidden: e.response?.status === 403,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, row.workshop_id, row.attendee_count, row.paid_count, authToken, retryCount]);

  const attendeeDetails = attendees.data;
  // One column per registration question, after the standard ones.
  const questions = answerColumns(attendeeDetails);
  const answerCells = (attendee) =>
    questions.map((field) => (
      <TableCell key={field.field_key}>{attendeeAnswer(attendee, field)}</TableCell>
    ));
  const hasAttendees =
    attendeeDetails &&
    (attendeeDetails.confirmed?.length > 0 || attendeeDetails.pending?.length > 0);

  return (
    <Fragment>
      <TableRow key={row.workshop_id} hover tabIndex={-1}>
        <TableCell align="center" component="th" scope="row">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={handleRowClick}
          >
            {open && !reset ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {row.workshop_id}
        </TableCell>
        <TableCell align="left" component="th" scope="row">
          {row.workshop_name}
        </TableCell>
        <TableCell align="left">
          {eventContext.TYPES.EVENT_TYPES.find(
            (t) => String(t.event_type_id) === String(row.event_type_id)
          )?.event_type_name || "—"}
        </TableCell>
        <TableCell align="center" component="th" scope="row">
          <Badge pill className={classes.successRatio}>
            ${row.fee}
          </Badge>
        </TableCell>
        <TableCell align="center">
          <Badge pill className={classes.greyRatio}>
            {moment(
              convertFromTimeStampTToDate(row.startdate),
              "YYYY-MM-DD"
            ).format("MMM D, YYYY")}
          </Badge>{" "}
          -{" "}
          <Badge pill className={classes.greyRatio}>
            {moment(
              convertFromTimeStampTToDate(row.enddate),
              "YYYY-MM-DD"
            ).format("MMM D, YYYY")}
          </Badge>
          {/* session_count: non-cancelled sessions (V101); absent from a
              server without sessions. */}
          {row.session_count > 0 && (
            <div className="text-muted small mt-1">
              {row.session_count} session{Number(row.session_count) === 1 ? "" : "s"}
            </div>
          )}
        </TableCell>
        <TableCell align="center" component="th" scope="row">
          {row.attendee_count ?? 0}
        </TableCell>
        {/* A free event has nothing to pay, so no paid/unpaid split. */}
        <TableCell align="center" component="th" scope="row">
          {free ? "—" : row.paid_count ?? 0}
        </TableCell>
        <TableCell align="center" component="th" scope="row">
          {free ? (
            "—"
          ) : row.unpaid_count > 0 ? (
            <Badge pill className={classes.dangerRatio}>
              {row.unpaid_count}
            </Badge>
          ) : (
            0
          )}
        </TableCell>
        <TableCell align="center" component="th" scope="row">
          <Badge
            pill
            className={row.student_only_ind ? classes.warningRatio : classes.greyRatio}
          >
            {row.student_only_ind ? "Student Only" : "Parent/Tutor"}
          </Badge>
        </TableCell>
        {showActions && (
          <TableCell align="center" style={{ whiteSpace: "nowrap", minWidth: 200 }}>
            <EventTableActions
              row={row}
              openEditEventPage={eventContext.openEditEventPage}
              openDeleteEventDialog={eventContext.openDeleteEventDialog}
              openActivateEventDialog={eventContext.openActivateEventDialog}
              openEventFullDialog={eventContext.openEventFullDialog}
              openDuplicateEventPage={eventContext.openDuplicateEventPage}
              openEventNotFullDialog={eventContext.openEventNotFullDialog}
              openEmailAttendeesPage={eventContext.openEmailAttendeesPage}
              onOpenAttendance={() => setAttendanceOpen(true)}
              onDownloadAttendees={downloadAttendees}
              downloadingExport={exportDownloading}
              exportError={exportError}
            />
          </TableCell>
        )}
      </TableRow>
      {showActions && (
        <EventAttendanceDialog
          row={row}
          show={attendanceOpen}
          onHide={() => setAttendanceOpen(false)}
        />
      )}

      <TableRow key={"details" + row.workshop_id}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box
              padding={2}
              margin={3}
              style={{ backgroundColor: "#f5f5f5", borderRadius: "0.5rem" }}
            >
              {attendees.status === "loading" || attendees.status === "idle" ? (
                <div className="d-flex align-items-center text-muted">
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Loading attendees…
                </div>
              ) : attendees.status === "error" ? (
                <p className="mb-0 text-danger">
                  {attendees.forbidden ? (
                    "You don't have access to this event's attendees."
                  ) : (
                    <>
                      Couldn't load attendees.{" "}
                      <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={retry}
                      >
                        Try again
                      </button>
                    </>
                  )}
                </p>
              ) : hasAttendees ? (
                // Scrolls sideways on its own once question columns make it
                // wider than the panel.
                <div className={classes.tableWrapper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Student ID</TableCell>
                        <TableCell>Parent Name</TableCell>
                        <TableCell>Parent Phone</TableCell>
                        <TableCell>Parent Email</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Method / Source</TableCell>
                        {questions.map((field) => (
                          <TableCell key={field.field_key} style={{ minWidth: 140 }}>
                            {field.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* id alone can repeat if a student holds two active
                          rows from before server V087's unique key. */}
                      {attendeeDetails.confirmed?.map((attendee, index) => (
                        <TableRow key={`confirmed-${attendee.id}-${index}`}>
                          <TableCell>{attendee.student_name}</TableCell>
                          <TableCell>{attendee.id}</TableCell>
                          <TableCell>{attendee.parent_name || "—"}</TableCell>
                          <TableCell>{attendee.parent_phone || "—"}</TableCell>
                          <TableCell>{attendee.parent_email || "—"}</TableCell>
                          {paymentCells(attendee)}
                          {answerCells(attendee)}
                        </TableRow>
                      ))}
                      {attendeeDetails.pending?.map((attendee, index) => (
                        <TableRow key={`pending-${index}`}>
                          <TableCell>{attendee.student_name}</TableCell>
                          <TableCell>
                            <Badge
                              pill
                              className={classes.warningRatio}
                              title="Pending web transaction"
                            >
                              Pending
                            </Badge>
                          </TableCell>
                          <TableCell>{attendee.parent_name || "—"}</TableCell>
                          <TableCell>{attendee.parent_phone || "—"}</TableCell>
                          <TableCell>{attendee.parent_email || "—"}</TableCell>
                          {paymentCells(attendee)}
                          {answerCells(attendee)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="mb-0 text-muted">No attendees yet.</p>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
